import { useEffect, useRef } from 'react'
import { listGisRasters } from '../../api/gisRasterApi'
import {
  TITILER_STYLE_CHANGED_EVENT,
  getResolvedTitilerStyleForLayer,
  getUserContextFromStorage,
} from '../../utils/layerTitilerStyleStorage'
import {
  fetchTitilerRasterSourceConfig,
  getTitilerBaseUrl,
} from '../../utils/titilerTileUrl'

const GIS_RASTER_PREFIX = 'gis-raster-'
const UPLOAD_EVENT = 'gis-raster-upload-success'
const DELETE_EVENT = 'gis-raster-deleted'
const VISIBILITY_EVENT = 'gis-raster-visibility-change'
const FLY_TO_EVENT = 'elda:fly-to-gis-raster'

// Mapbox 3.x throws "Style is not done loading" if getStyle() is called before
// the style has parsed. Wrap defensively — callers want falsy when not ready.
const safeGetStyle = map => {
  try {
    return map.getStyle()
  } catch {
    return null
  }
}

/**
 * Fetches GIS-uploaded rasters and renders them on the Mapbox map via TiTiler
 * COG tiles. Mirrors the pattern used by `useBaseLayerVisibility` for lidar
 * snow layers, but operates on the separate `/gis/raster` API.
 *
 * Visibility is toggled by dispatching a `gis-raster-visibility-change`
 * CustomEvent with `detail: { layerId, visible }`.
 */
const useGisRasterMapLayers = (mapRef, operationalLayersLoaded) => {
  const mountedIdsRef = useRef(new Set())
  const layerVisRef = useRef(new Map())
  const layerOpacityRef = useRef(new Map())
  const titilerBase = getTitilerBaseUrl()

  // Main effect: fetch rasters and add/remove map sources+layers
  useEffect(() => {
    if (!mapRef.current || !operationalLayersLoaded) return

    let cancelled = false
    const map = mapRef.current

    const addRastersToMap = async () => {
      let rasters = []
      try {
        const resp = await listGisRasters()
        rasters = (resp.rasters || []).filter(
          r => r.status === 'ready' && r.s3_url,
        )
      } catch (err) {
        console.warn('[gis-raster-map] list failed', err)
        return
      }

      if (cancelled || !safeGetStyle(map)) return

      const desiredIds = new Set(
        rasters.map(r => `${GIS_RASTER_PREFIX}${r.id}`),
      )

      // Remove layers that are no longer in the list
      for (const id of mountedIdsRef.current) {
        if (!desiredIds.has(id)) {
          try {
            if (map.getLayer(id)) map.removeLayer(id)
            if (map.getSource(id)) map.removeSource(id)
          } catch (e) {
            console.warn('[gis-raster-map] cleanup failed', id, e)
          }
          mountedIdsRef.current.delete(id)
          layerVisRef.current.delete(id)
          layerOpacityRef.current.delete(id)
        }
      }

      // Add new raster layers
      const { userId, resortId } = getUserContextFromStorage()

      for (const layer of rasters) {
        const id = `${GIS_RASTER_PREFIX}${layer.id}`
        if (cancelled) return
        if (mountedIdsRef.current.has(id)) continue

        try {
          const styleOverride = getResolvedTitilerStyleForLayer(
            layer,
            userId,
            resortId,
          )
          const cfg = await fetchTitilerRasterSourceConfig(
            layer.s3_url,
            titilerBase,
            {
              minzoom: 0,
              layer,
              ...(styleOverride ? { styleOverride } : {}),
            },
          )

          if (cancelled || !cfg || !safeGetStyle(map)) return
          if (map.getSource(id)) continue // another async won the race

          map.addSource(id, {
            type: 'raster',
            tiles: cfg.tiles,
            tileSize: cfg.tileSize,
            ...(cfg.minzoom != null && { minzoom: cfg.minzoom }),
            ...(cfg.maxzoom != null && { maxzoom: cfg.maxzoom }),
            ...(cfg.bounds && { bounds: cfg.bounds }),
          })

          // GIS rasters default to hidden (user toggles via eye icon)
          const vis = layerVisRef.current.get(id) ?? false
          const opacityVal =
            styleOverride &&
            typeof styleOverride.opacity === 'number' &&
            Number.isFinite(styleOverride.opacity) &&
            styleOverride.opacity >= 0 &&
            styleOverride.opacity <= 1
              ? styleOverride.opacity
              : 1
          layerOpacityRef.current.set(id, opacityVal)
          // We use raster-opacity (0 = hidden, opacityVal = shown) instead of
          // layout.visibility because Mapbox raster sources don't fetch tiles
          // until a visible layer needs them. Using opacity keeps the layer
          // "active" so tiles are pre-fetched, making toggles instant and
          // race-free. The layer is always layout-visible.
          map.addLayer({
            id,
            type: 'raster',
            source: id,
            paint: {
              'raster-opacity': vis ? opacityVal : 0,
              'raster-fade-duration': 0,
              'raster-resampling': cfg.rasterResampling ?? 'linear',
            },
            layout: { visibility: 'visible' },
          })

          mountedIdsRef.current.add(id)

          // Position after satellite layer for correct z-order
          try {
            const style = safeGetStyle(map)
            const satIdx = (style?.layers || []).findIndex(
              l => l.id === 'satellite',
            )
            if (satIdx !== -1 && satIdx + 1 < style.layers.length) {
              map.moveLayer(id, style.layers[satIdx + 1].id)
            }
          } catch {
            /* non-critical ordering */
          }
        } catch (err) {
          console.warn('[gis-raster-map] add failed for', layer.id, err)
        }
      }
    }

    const run = () => {
      addRastersToMap().catch(e =>
        console.warn('[gis-raster-map] run error', e),
      )
    }

    // Mapbox drops all custom layers/sources whenever a new basemap style is
    // loaded. Clear our mount tracking on every style.load so addRastersToMap
    // re-adds them (visibility preserved via layerVisRef).
    const handleStyleLoad = () => {
      if (cancelled) return
      mountedIdsRef.current.clear()
      run()
    }
    map.on('style.load', handleStyleLoad)

    // Try to run immediately if the style spec is loaded. getStyle() throws
    // "Style is not done loading" in Mapbox 3.x when called before the style
    // is parsed, so wrap defensively. isStyleLoaded() is stricter (false while
    // sources are still loading) but safe to call. Fallback: subscribe to
    // 'idle' AND poll briefly in case both events were missed.
    if (safeGetStyle(map) || map.loaded()) {
      run()
    } else {
      const onceIdle = () => {
        if (cancelled) return
        run()
      }
      map.once('idle', onceIdle)
      let retries = 0
      const poll = () => {
        if (cancelled || retries >= 30) return
        retries += 1
        if (safeGetStyle(map) || map.loaded()) {
          map.off('idle', onceIdle)
          run()
        } else {
          setTimeout(poll, 200)
        }
      }
      setTimeout(poll, 200)
    }

    // Re-fetch when a raster is uploaded or deleted (mountedIdsRef preserved).
    window.addEventListener(UPLOAD_EVENT, run)
    window.addEventListener(DELETE_EVENT, run)

    // When a TiTiler style changes, clear all mounted GIS raster layers/sources
    // so they are re-added with fresh tile URLs reflecting the new style.
    const onStyleChanged = () => {
      if (cancelled) return
      for (const id of mountedIdsRef.current) {
        try {
          if (map.getLayer(id)) map.removeLayer(id)
          if (map.getSource(id)) map.removeSource(id)
        } catch (e) {
          console.warn(
            '[gis-raster-map] failed to clear layer on style change',
            id,
            e,
          )
        }
      }
      mountedIdsRef.current.clear()
      run()
    }
    window.addEventListener(TITILER_STYLE_CHANGED_EVENT, onStyleChanged)

    return () => {
      cancelled = true
      try {
        map.off('style.load', handleStyleLoad)
      } catch {
        /* map may be destroyed */
      }
      window.removeEventListener(UPLOAD_EVENT, run)
      window.removeEventListener(DELETE_EVENT, run)
      window.removeEventListener(TITILER_STYLE_CHANGED_EVENT, onStyleChanged)
    }
  }, [mapRef, operationalLayersLoaded, titilerBase])

  // Visibility sync via custom event
  useEffect(() => {
    const handler = e => {
      const { layerId, visible } = e.detail || {}
      if (layerId == null) return

      const id = `${GIS_RASTER_PREFIX}${layerId}`
      layerVisRef.current.set(id, !!visible)

      const map = mapRef.current
      if (map && map.getLayer(id)) {
        const targetOpacity = visible ? layerOpacityRef.current.get(id) ?? 1 : 0
        map.setPaintProperty(id, 'raster-opacity', targetOpacity)
      }
    }

    window.addEventListener(VISIBILITY_EVENT, handler)
    return () => window.removeEventListener(VISIBILITY_EVENT, handler)
  }, [mapRef])

  // Fly-to (fit bounds) sync via custom event. Also makes the raster visible.
  useEffect(() => {
    const handler = e => {
      const map = mapRef.current
      const { layerId } = e.detail || {}
      if (!map || layerId == null) return

      const id = `${GIS_RASTER_PREFIX}${layerId}`

      // mark visible (mirrors dispatchGisRasterVisibility(true))
      layerVisRef.current.set(id, true)
      if (map.getLayer(id)) {
        const targetOpacity = layerOpacityRef.current.get(id) ?? 1
        map.setPaintProperty(id, 'raster-opacity', targetOpacity)
      }

      const source = map.getSource(id)
      const bounds = source?.bounds
      if (
        !Array.isArray(bounds) ||
        bounds.length !== 4 ||
        !bounds.every(Number.isFinite)
      ) {
        return
      }
      const [west, south, east, north] = bounds
      map.fitBounds(
        [
          [west, south],
          [east, north],
        ],
        { padding: 60, duration: 800 },
      )
    }

    window.addEventListener(FLY_TO_EVENT, handler)
    return () => window.removeEventListener(FLY_TO_EVENT, handler)
  }, [mapRef])
}

export default useGisRasterMapLayers

/**
 * Dispatch this from GISDashboard (or any component) to toggle a GIS raster's
 * map visibility without prop-drilling through map.jsx.
 */
export function dispatchGisRasterVisibility(layerId, visible) {
  window.dispatchEvent(
    new CustomEvent(VISIBILITY_EVENT, {
      detail: { layerId, visible },
    }),
  )
}

/**
 * Dispatch after deleting a GIS raster so the hook re-fetches and cleans up.
 */
export function dispatchGisRasterDeleted() {
  window.dispatchEvent(new CustomEvent(DELETE_EVENT))
}

/**
 * Request the map to fit the bounds of a GIS raster (and make it visible).
 * Used by smart-search: clicking a raster hit should both reveal and zoom.
 */
export function dispatchGisRasterFlyTo(layerId) {
  if (layerId == null) return
  window.dispatchEvent(new CustomEvent(FLY_TO_EVENT, { detail: { layerId } }))
}
